import { Dispatch, SetStateAction, useEffect, useState } from "react"
import type { Blockgroup } from "db"
import { twMerge } from "tailwind-merge"
import { useTranslation } from "react-i18next"

import { pick } from "utils"
import { HelpTooltip } from "ui"

import {
  HEAT_DISPARITY,
  TREND_CHART_TEMPLATES,
  TREND_CHART_TEMPLATES_MAP,
  TrendChartData,
  TREND_CHART_X_OPTIONS,
  TREND_CHART_Y_OPTIONS,
  TREE_CANOPY_COVER,
  TREES_PER_PERSON,
} from "../../dashboard.constants"

import {
  getTrendChartData,
  FormatedDiff,
  getStatesWithoutDataString,
  hasNoDataForStateAndAttribute,
} from "./utils"
import { ChartOption, ChartSelector } from "./chart-helpers"
import { BarChart } from "./bar-chart"
import type { BarChartData, BarChartProps } from "./bar-chart"

const Chart = ({
  x,
  y,
  setX,
  setY,
  data,
  ...props
}: {
  className?: string
  chartClassName?: string
  x: ChartOption
  y: ChartOption
  setY: Dispatch<SetStateAction<ChartOption>>
  setX: Dispatch<SetStateAction<ChartOption>>
} & BarChartProps) => {
  const { t } = useTranslation(["location-insights"])
  const cx = "xl:mb-0 mb-8 h-full flex flex-col"
  const printCX = "print:mb-8 print:mr-0 print:w-full"

  const titleCX = "text-xl text-gray-700 font-semibold"
  const barChartCX = "h-full bg-white border border-gray-200 rounded-md mt-4"
  return Boolean(BarChart) ? (
    <div className={twMerge(cx, printCX, props.className)}>
      <div className={titleCX}>
        <ChartSelector
          selectedChartOption={y}
          setSelectedChartOption={setY}
          options={TREND_CHART_Y_OPTIONS}
          help={t("location-insights:charts.choose_canopy_hint")}
        />{" "}
        <span className="mx-4"> & </span>{" "}
        <ChartSelector
          selectedChartOption={x}
          setSelectedChartOption={setX}
          options={TREND_CHART_X_OPTIONS}
        />
      </div>
      <div className={twMerge(barChartCX, props.chartClassName)}>
        {Boolean(data) && <BarChart {...{ data, ...props }} />}
      </div>
    </div>
  ) : null
}

const QuintileCompareText = ({
  x,
  y,
  data,
  isStateWithoutData,
}: {
  x: ChartOption
  y: ChartOption
  data: BarChartData[]
  isStateWithoutData: boolean
}) => {
  const { t, i18n } = useTranslation(["location-insights"])
  const quintile = data[0]
  const compareToQuintile = data[x.value === HEAT_DISPARITY ? data.length - 2 : data.length - 1]
  const cx = "text-xl mb-14"
  const emphasisCx = "text-brand-blue-dark font-medium"
  const zeroValues = data.map(pick("value")).every((d) => d === 0)
  const noDataStates =
    isStateWithoutData &&
    getStatesWithoutDataString({ currentLanguage: i18n.language, attribute: x.value })

  if ((quintile.binSize <= 1 || compareToQuintile.binSize <= 1) && !zeroValues)
    return (
      <p className={twMerge(cx, emphasisCx, "mb-32 mt-8 py-2 text-sm")}>
        {t("location-insights:charts.inconclusive_data")}{" "}
        <HelpTooltip className="cursor-pointer">
          <p className="text-left">{t("location-insights:charts.no_data_explanation")}</p>
        </HelpTooltip>
      </p>
    )

  if (data.map((d) => d.binSize).every((binSize) => binSize == 0))
    return (
      <p className={twMerge(cx, emphasisCx, "text-sm")}>
        {isStateWithoutData
          ? t("location-insights:charts.no_state_data_available", { noDataStates })
          : t("location-insights:charts.no_data_available")}
      </p>
    )

  const diff =
    y.value === TREE_CANOPY_COVER
      ? quintile.value / compareToQuintile.value
      : quintile.value - compareToQuintile.value

  const prefix =
    x.value === HEAT_DISPARITY
      ? t("location-insights:charts.cooler_neighborhoods")
      : t("location-insights:charts.less_neighborhoods", {
          indicator: t(`facets:${x?.value}.name`)?.toLowerCase(),
        })
  const suffix =
    x.value === HEAT_DISPARITY
      ? t("location-insights:charts.compared_to_hotter")
      : t("location-insights:charts.compared_to_more")
  const tooltip =
    y.value === TREE_CANOPY_COVER
      ? x.value === HEAT_DISPARITY
        ? t("location-insights:charts.tooltip_heat")
        : t("location-insights:charts.tooltip_percentile")
      : t("location-insights:charts.tooltip_difference")

  return (
    <p className={cx}>
      {prefix}{" "}
      <span className={emphasisCx}>
        <FormatedDiff diff={diff} type={y.value} />
        <HelpTooltip className="cursor-pointer">
          <p className="text-left">{tooltip}</p>
        </HelpTooltip>{" "}
      </span>{" "}
      {suffix}
    </p>
  )
}

export const Charts = ({
  title,
  shortTitle,
  blockgroups,
}: {
  title: string
  shortTitle: string
  blockgroups: Blockgroup[]
}) => {
  const { t } = useTranslation(["location-insights", "facets"])
  const flexCenterCX = "justify-center items-center"
  const [y, setY] = useState<ChartOption>(TREND_CHART_Y_OPTIONS[0])
  const [x, setX] = useState<ChartOption>(TREND_CHART_X_OPTIONS[0])
  const isStateWithoutData = hasNoDataForStateAndAttribute({
    attribute: x.value,
    stateCode: blockgroups[0]?.state,
  })

  /**
   * filter out blockgroups with low populations to avoid data issues, only applied
   * when doing a trees per person chart
   */
  let filteredBlockgroups: Blockgroup[]
  if (y.value === TREES_PER_PERSON) {
    const MIN_POPULATION = 150
    filteredBlockgroups = blockgroups.filter((bg) => bg.clipped_bg_population! > MIN_POPULATION)
  } else {
    filteredBlockgroups = blockgroups
  }

  const [trendChartData, setTrendChartData] = useState<TrendChartData>(
    getTrendChartData(
      title,
      shortTitle,
      filteredBlockgroups,
      TREND_CHART_TEMPLATES[0],
      y.value as typeof TREE_CANOPY_COVER | typeof TREES_PER_PERSON
    )
  )

  useEffect(() => {
    const chartTemplate = TREND_CHART_TEMPLATES_MAP.get(x.value as keyof Blockgroup)!
    const data = getTrendChartData(
      title,
      shortTitle,
      filteredBlockgroups,
      chartTemplate,
      y.value as typeof TREE_CANOPY_COVER | typeof TREES_PER_PERSON
    )
    setTrendChartData(data)
  }, [x, y])

  return (
    <section className={twMerge(flexCenterCX, "m-auto flex-col")}>
      <header
        className={twMerge(
          "w-full pb-16 text-center text-brand-green-darker xl:pb-12",
          "m-auto w-full bg-white shadow-[0_-10px_12px_-10px_rgba(0,0,0,0.125)] xl:w-4/5"
        )}
      >
        <h1 className="pt-16 text-caption font-extrabold uppercase">
          {t("location-insights:charts.title")}
        </h1>
        <h2 className="text-2xl font-medium tracking-wide text-gray-700">{title}</h2>
        <p className="font-base m-auto pt-2 text-center text-base text-gray-700">
          {t("location-insights:charts.subtitle")}
        </p>
      </header>
      <section
        className={twMerge(
          "mx-auto grid w-full grid-cols-1 gap-10 bg-white xl:w-4/5 xl:grid-cols-5"
        )}
      >
        <div
          className={twMerge(
            "col-span-1 m-auto w-11/12 pl-0 md:w-4/5 md:pl-10 xl:col-span-2 xl:w-full xl:pl-20",
            "xl:text-right"
          )}
        >
          <section className="text-gray-700">
            <header className={twMerge("font-bold")}>
              <h1
                className={twMerge(
                  "mt-4 text-[0.9rem] uppercase tracking-wide text-brand-green-darker lg:-mt-10"
                )}
              >
                {t("location-insights:charts.canopy_trends")}
              </h1>
              <h2 className={twMerge("mb-8 text-3xl text-brand-green-darker")}>
                {t("location-insights:charts.where_trees")}
              </h2>
            </header>
            <QuintileCompareText
              x={x}
              y={y}
              data={trendChartData.data}
              isStateWithoutData={isStateWithoutData}
            />
          </section>
          <section className="text-sm text-brand-green-darker">
            <h2 className="border-t border-gray-200 pt-2 font-bold uppercase">
              {t("location-insights:charts.how_to_read")}
            </h2>
            <p>
              {t("location-insights:charts.explanation", {
                indicator: t(`facets:${x?.value}.name`)?.toLowerCase(),
              })}{" "}
              <HelpTooltip className="cursor-pointer">
                <p className="text-left">
                  {t(TREND_CHART_TEMPLATES_MAP.get(x.value as keyof Blockgroup)!.help)}
                </p>
              </HelpTooltip>
              <HelpTooltip className="cursor-pointer">
                <p className="text-left">{t("location-insights:charts.choose_canopy_hint")}</p>
              </HelpTooltip>
            </p>
          </section>
        </div>
        <div
          className={twMerge(
            "col-span-1 m-auto w-11/12 pr-0 pt-12 md:w-4/5 md:pr-10 xl:col-span-3 xl:w-full xl:pr-20"
          )}
        >
          <div className="flex">
            <p className="flex-inline -mr-3 hidden text-annotation xl:block">
              {t("location-insights:charts.choose_canopy")}&nbsp;
              <HelpTooltip className="cursor-pointer">
                <p className="text-left">{t("location-insights:charts.choose_canopy_hint")}</p>
              </HelpTooltip>
            </p>
            <p className="flex-inline ml-[7.8rem] hidden text-annotation xl:block">
              {t("location-insights:charts.choose_indicator")}{" "}
              <HelpTooltip className="cursor-pointer">
                <p className="text-left">
                  {t(TREND_CHART_TEMPLATES_MAP.get(x.value as keyof Blockgroup)!.help)}
                </p>
              </HelpTooltip>
            </p>
          </div>
          <Chart
            x={x}
            y={y}
            setX={setX}
            setY={setY}
            {...{
              ...trendChartData,
              labelClassName:
                "whitespace-normal w-24 text-center text-gray-700 font-medium underline decoration-dotted decoration-1",
            }}
          />
        </div>
      </section>
    </section>
  )
}
